import {z} from 'zod';

export const zCytoscapeNodeData = z.object({
	id: z.string(),
	label: z.string().nullable(),
	personId: z.string().nullable(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	birthDate: z.string().nullable(),
	deathDate: z.string().nullable(),
	gender: z.enum(['M', 'F']).nullable(),
	profileImageUrl: z.string().nullable(),
});
export type CytoscapeNodeData = z.infer<typeof zCytoscapeNodeData>;

export const zCytoscapeEdgeData = z.object({
	id: z.string().nullable(),
	source: z.string(),
	target: z.string(),
	label: z.string().nullable(),
});
export type CytoscapeEdgeData = z.infer<typeof zCytoscapeEdgeData>;

export const zCytoscapeStyleRule = z.object({
	selector: z.string(),
	style: z.record(z.string(), z.unknown()),
});
export type CytoscapeStyleRule = z.infer<typeof zCytoscapeStyleRule>;

/**
 * LLM output style rule — array of {name, value} pairs (strict-mode safe).
 */
export const zCytoscapeStyleProperty = z.object({
	name: z.string(),
	value: z.string(),
});
export type CytoscapeStyleProperty = z.infer<typeof zCytoscapeStyleProperty>;

export const zCytoscapeStyleRuleOutput = z.object({
	selector: z.string(),
	style: z.array(zCytoscapeStyleProperty),
});
export type CytoscapeStyleRuleOutput = z.infer<typeof zCytoscapeStyleRuleOutput>;

/**
 * Schema for LLM structured output (strict mode).
 * Separate nodes/edges arrays — positions are NOT included; the server stamps them.
 */
export const zCytoscapeOutputSchema = z.object({
	nodes: z.array(z.object({ data: zCytoscapeNodeData })),
	edges: z.array(z.object({ data: zCytoscapeEdgeData })),
	style: z.array(zCytoscapeStyleRuleOutput),
});
export type CytoscapeOutput = z.infer<typeof zCytoscapeOutputSchema>;

export function cytoscapeStyleOutputToStored(
	styleRules: CytoscapeStyleRuleOutput[]
): CytoscapeStyleRule[] {
	return styleRules.map((rule) => ({
		selector: rule.selector,
		style: Object.fromEntries(rule.style.map((p) => [p.name, p.value])),
	}));
}

/**
 * Stored element format — grouped by type, positions stamped server-side.
 */
export const zCytoscapeNodeElement = z.object({
	data: zCytoscapeNodeData,
	position: z.object({x: z.number(), y: z.number()}),
	locked: z.boolean().nullable(),
});
export type CytoscapeNodeElement = z.infer<typeof zCytoscapeNodeElement>;

export const zCytoscapeEdgeElement = z.object({
	data: zCytoscapeEdgeData,
});
export type CytoscapeEdgeElement = z.infer<typeof zCytoscapeEdgeElement>;

export const zCytoscapeElement = z.union([zCytoscapeNodeElement, zCytoscapeEdgeElement]);
export type CytoscapeElement = z.infer<typeof zCytoscapeElement>;

export const zCytoscapeElements = z.object({
	nodes: z.array(zCytoscapeNodeElement),
	edges: z.array(zCytoscapeEdgeElement),
});
export type CytoscapeElements = z.infer<typeof zCytoscapeElements>;

export const zLayoutSchema = z.discriminatedUnion('name', [
	z.object({ name: z.literal('preset'), fit: z.boolean().optional(), padding: z.number().optional() }),
	z.object({ name: z.literal('random') }),
	z.object({ name: z.literal('breadthfirst'), directed: z.boolean().optional(), direction: z.string().optional() }),
	z.object({ name: z.literal('cose') }),
	z.object({ name: z.literal('circle'), clockwise: z.boolean().optional(), radius: z.number().optional(), startAngle: z.number().optional() }),
	z.object({ name: z.literal('grid'), rows: z.number().optional(), cols: z.number().optional() }),
	z.object({ name: z.literal('concentric'), startAngle: z.number().optional(), clockwise: z.boolean().optional() }),
]);

export const zCytoscapeJson = z.object({
	elements: zCytoscapeElements,
	style: z.array(zCytoscapeStyleRule),
	layout: zLayoutSchema.nullish(),
});

/**
 * Root stored JSON shape: { background: assetId | null, cytoscape: { elements, style, layout? } }
 */
export const zCytoscapeAssetJson = z.object({
	background: z.string().nullable(),
	cytoscape: zCytoscapeJson,
});
export type CytoscapeAssetJson = z.infer<typeof zCytoscapeAssetJson>;